import { useCallback, useEffect, useRef } from 'react';
import { useBlocker, type BlockerFunction } from 'react-router-dom';
import type { ConfirmRequest } from '@/pages/fulfillment/components/usePromptDialog';
import type { LineDraft, LineEditField } from '@/utils/fulfillmentLineEdit';

/** What the page asks before a route change throws an edit away (SSD-7701). */
export const LEAVE_REQUEST: ConfirmRequest = {
  title: 'Unsaved changes',
  message: 'You have unsaved changes. Leave this page?',
  confirmLabel: 'Leave page',
  cancelLabel: 'Stay',
};

/** Every place Order Detail holds an edit only in memory; each says "changed" against what it opened with. */
export interface OrderDetailEdits {
  /** The open row and what it opened with; both null when no row is open. */
  lineDraft: LineDraft | null;
  lineBase: LineDraft | null;
  memoDrafts: Record<number, string>;
  customsChanged: boolean;
  commentText: string;
  addressChanged: boolean;
  shelfChanged: boolean;
  /** The outbound waybill form opens on empty boxes, so anything in them was typed. */
  outboundWaybillTyped: boolean;
  picChanged: boolean;
}

const lineDraftChanged = (draft: LineDraft | null, base: LineDraft | null): boolean =>
  draft !== null &&
  base !== null &&
  (Object.keys(draft) as LineEditField[]).some((field) => draft[field] !== base[field]);

/** True only when leaving would lose something; opening a form and changing nothing is not an edit. */
export const hasUnsavedEdits = (edits: OrderDetailEdits): boolean =>
  lineDraftChanged(edits.lineDraft, edits.lineBase) ||
  Object.values(edits.memoDrafts).some((text) => text.trim() !== '') ||
  edits.customsChanged ||
  edits.commentText.trim() !== '' ||
  edits.addressChanged ||
  edits.shelfChanged ||
  edits.outboundWaybillTyped ||
  edits.picChanged;

/** The browser's own warning on refresh, tab close or a typed URL, listening only while something is unsaved. */
export function UnloadWarning({ active }: { active: boolean }): null {
  useEffect(() => {
    if (!active) {
      return undefined;
    }
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [active]);
  return null;
}

/** The browser's Navigation API, where it exists: it can stop Back and Forward before they move at all. */
interface HistoryMove extends Event {
  navigationType: string;
  userInitiated: boolean;
  destination: { key: string; sameDocument: boolean; url: string };
}
interface BrowserNavigation extends EventTarget {
  traverseTo(key: string): { finished: Promise<unknown> };
}

/**
 * Asks before any route change while something is unsaved: browser Back and Forward, the menu, and every link on the
 * page or in its dialogs (SSD-7815). A new query string on the same page keeps the edit, so it passes. The page renders
 * it on every view, the error view too (SSD-7850).
 *
 * A question the page already has open stays open and the move is dropped: a dialog stops links but not Back, and
 * asking over it would throw away what was typed there.
 */
export function LeaveBlocker({
  active,
  questionOpen,
  askConfirm,
  dismissQuestion,
}: {
  active: boolean;
  questionOpen: boolean;
  askConfirm: (request: ConfirmRequest) => Promise<boolean>;
  dismissQuestion: () => void;
}): null {
  // Back and Forward reach the other history entry before the router can refuse them, so the browser scrolls there and
  // back. Bootstrap's smooth root scrolling stretches the way back to half a second; without it, it takes a frame.
  useEffect(() => {
    if (!active) {
      return undefined;
    }
    const { style } = document.documentElement;
    const before = style.scrollBehavior;
    style.scrollBehavior = 'auto';
    return () => {
      style.scrollBehavior = before;
    };
  }, [active]);
  // An agreed Back or Forward goes by the router too, and this lets it through.
  const leaving = useRef(false);
  const blocker = useBlocker(
    useCallback<BlockerFunction>(
      ({ currentLocation, nextLocation }) =>
        active && !leaving.current && currentLocation.pathname !== nextLocation.pathname,
      [active],
    ),
  );
  const latest = useRef(blocker);
  latest.current = blocker;
  const asking = useRef(false);
  // The router drops the blocker with this component, and answering one it has dropped throws.
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  // Stops Back and Forward before the browser moves, so the page and the address stay still and only the question
  // shows. The browser allows this after a click or a key press; otherwise the router's blocker below still asks.
  useEffect(() => {
    const { navigation } = window as unknown as { navigation?: BrowserNavigation };
    if (!active || !navigation) {
      return undefined;
    }
    const onNavigate = (event: Event) => {
      const move = event as HistoryMove;
      // Only the person's own press: the router undoes a refused move with a history move of its own, which must pass.
      const crossesPage =
        move.userInitiated &&
        move.navigationType === 'traverse' &&
        move.destination.sameDocument &&
        new URL(move.destination.url).pathname !== window.location.pathname;
      if (!crossesPage || !move.cancelable || leaving.current) {
        return;
      }
      move.preventDefault();
      if (questionOpen || asking.current) {
        return;
      }
      asking.current = true;
      const { key } = move.destination;
      void askConfirm(LEAVE_REQUEST).then((agreed) => {
        asking.current = false;
        if (!agreed || !mounted.current) {
          return;
        }
        // Stays on until the route change unmounts the guard: `finished` can settle before the router hears of the move.
        leaving.current = true;
        const guardAgain = () => {
          leaving.current = false;
        };
        try {
          navigation.traverseTo(key).finished.catch(guardAgain);
        } catch {
          guardAgain();
        }
      });
    };
    navigation.addEventListener('navigate', onNavigate);
    return () => navigation.removeEventListener('navigate', onNavigate);
  }, [active, questionOpen, askConfirm]);
  const blocked = blocker.state === 'blocked';
  // Set while the open question is about the move the router is holding.
  const askingForHeldMove = useRef(false);
  useEffect(() => {
    if (!blocked) {
      // The router let the held move go (another move finished), so the question has nothing left to answer: Stay.
      if (askingForHeldMove.current) {
        dismissQuestion();
      }
      return;
    }
    if (asking.current) {
      return;
    }
    if (questionOpen) {
      latest.current.reset?.();
      return;
    }
    asking.current = true;
    askingForHeldMove.current = true;
    // The answer belongs to the move asked about: a second held move replaces the router's blocker, not this one.
    const askedMove = latest.current.proceed;
    void askConfirm(LEAVE_REQUEST).then((agreed) => {
      asking.current = false;
      askingForHeldMove.current = false;
      if (!mounted.current || latest.current.state !== 'blocked') {
        return;
      }
      if (agreed) {
        askedMove?.();
      } else {
        latest.current.reset?.();
      }
    });
  }, [blocked, questionOpen, askConfirm, dismissQuestion]);
  return null;
}
